import { hashDecklist } from './deckTitle'
import { tc } from '../i18n'

// Platform names our importers write to the `source` column - matches what
// ArchidektDeckPicker/useDeckImportQueue already write, NOT the API's
// /decks/fetch-url `source_type` values ('archidekt_deck' etc - see
// mapFetchedSourceType below).
// SlingLab is deliberately absent: its playtest URL carries the decklist
// itself, so there is no hosted deck to refresh from. See
// LINKABLE_PLATFORMS in utils/urlDetector.ts.
export type LinkableSource = 'archidekt' | 'moxfield' | 'manabox' | 'deckcheck' | 'commandersalt' | 'hareruya' | 'edhrec'

// A resolved deck link, ready to attach to a saveDeck `opts.link` (minus the
// content hash, which is computed at save time against whatever decklist
// string is actually about to be persisted - see BracketPage's Task 10
// wiring).
export interface FetchedDeckLink {
  sourceUrl: string
  source: LinkableSource
}

/**
 * Map the API's /decks/fetch-url `source_type` field (e.g. 'archidekt_deck')
 * to the platform name our importers write to `source` ('archidekt'). Returns
 * null for anything unrecognized, so a future/unexpected source_type can
 * never accidentally write a link tagged with the wrong platform - or any
 * link at all, since callers treat a null return as "don't link this save."
 */
export function mapFetchedSourceType(sourceType: string): LinkableSource | null {
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (sourceType === 'archidekt_deck') return 'archidekt'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (sourceType === 'moxfield_deck') return 'moxfield'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (sourceType === 'manabox_deck') return 'manabox'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (sourceType === 'deckcheck_deck') return 'deckcheck'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (sourceType === 'commandersalt_deck') return 'commandersalt'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (sourceType === 'hareruya_deck') return 'hareruya'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (sourceType === 'edhrec_deck') return 'edhrec'
  // 'slinglab_deck' falls through to null ON PURPOSE, and must keep doing so.
  // A SlingLab playtest URL encodes the decklist itself as a blob - the blob
  // IS the deck, not a pointer at a page we could re-read - so there is no
  // source to refresh from and a stored link would only ever go stale.
  return null
}

// Shared shape for the divergence check - deliberately NOT `SavedDeck` or
// `DetailDeck` directly, so this stays usable from both (they carry the same
// three fields under the same names, but are otherwise different types).
export interface DivergenceCheckable {
  sourceUrl?: string | null
  sourceDecklistHash?: string | null
  decklist?: string
}

/**
 * Has this linked deck been edited in Commander Bracket since its last pull
 * from source? Decided by comparing the CONTENT hash of the stored decklist
 * against the hash recorded at the last successful pull - never by comparing
 * timestamps. `updatedAt`/`sourceSyncedAt` also move on a rename or a share
 * toggle, so a timestamp comparison would tell a user who merely renamed
 * their deck that they had edited its card list. See
 * `docs/superpowers/plans/2026-08-04-archidekt-deck-sync` Task 8 for the
 * design rationale.
 */
export function isDeckDiverged(deck: DivergenceCheckable): boolean {
  return Boolean(
    deck.sourceUrl &&
    deck.sourceDecklistHash &&
    deck.decklist &&
    hashDecklist(deck.decklist) !== deck.sourceDecklistHash,
  )
}

/**
 * Best-effort "is this refresh failure a dead link" check.
 *
 * `ImportResult` (from `useDeckImportQueue`) carries only a plain `error`
 * string, not a status code - `fetchDeckFromUrl` wraps a non-2xx response in
 * `ApiError`, whose `.message` is `API Error <status>: <statusText>`, so a 404
 * shows up as the literal substring "404" in that message. This is genuinely
 * the only signal available without widening `ImportResult`'s shape (out of
 * scope here - `useDeckImportQueue` is done and reviewed), so it is not
 * bulletproof: a decklist fetch that happened to fail with an unrelated error
 * whose text contains "404" would false-positive. Acceptable because the
 * consequence of a false positive is just an extra "Link broken" chip with an
 * Unlink button - never a deleted deck.
 *
 * A deck made PRIVATE on the source is ALSO a broken link by the spec's error
 * table, but the API maps that case to a 403-from-upstream -> 502 response
 * (app/main.py's fetch_decklist_from_url), never a 404 - a private deck still
 * exists, it just can't be read. The status code alone can't distinguish that
 * from a genuine upstream 502 outage, so `useDeckImportQueue` appends the
 * response body's parsed `detail` to the error string when present
 * (describeImportError), and every source phrases that detail identically:
 * "<Source> access denied. The deck may be private." Match on that text too.
 */
export function isBrokenLinkError(error?: string, code?: string | null): boolean {
  // The machine code first (i18n 3-api.2): a localized API response marks a
  // private deck with `code: "source_private"`, whatever language the rest of
  // the response is in. English responses never carry it, so the text checks
  // below stay the live path for them (and for API builds older than the
  // code).
  if (code === SOURCE_PRIVATE_CODE) return true
  if (!error) return false
  return /\b404\b/.test(error) || /access denied/i.test(error) || /may be private/i.test(error)
}

/** The API's machine code for a deck the source refuses to share. */
export const SOURCE_PRIVATE_CODE = 'source_private'

/**
 * Human label for a deck's source platform, for UI copy ("Linked to
 * Archidekt", "Pulling from Moxfield will replace it..."). The fallback keeps
 * this forward-compatible without a hardcoded exhaustive list.
 */
export function sourceLabel(source?: string | null): string {
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (source === 'archidekt') return 'Archidekt'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (source === 'moxfield') return 'Moxfield'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (source === 'manabox') return 'ManaBox'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (source === 'deckcheck') return 'DeckCheck'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (source === 'commandersalt') return 'CommanderSalt'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (source === 'hareruya') return 'Hareruya'
  // eslint-disable-next-line i18next/no-literal-string -- source code or platform brand name
  if (source === 'edhrec') return 'EDHREC'
  if (!source) return tc('deck_source.unknown')
  return source.charAt(0).toUpperCase() + source.slice(1)
}
